import * as React from 'react';
import clsx from 'clsx';
import { getToken } from '@workiva/unify-tokens';
import { styled } from '@mui/material';

import fileListItemClasses from 'src/components/FileListItem/fileListItemClasses';
import {
  type FileModel,
  defaultFileUploadPercentComplete,
  fileIsUploading,
  fileUploadIsIndeterminate,
} from 'src/components/FileUploader/fileModel';
import { Typography, type TypographyProps } from 'src/components/Typography';
import formatFileSize from 'src/utils/formatFileSize';

export type FileListItemFileMetaOwnProps = {
  showFileSize: boolean;
};

export type FileListItemFileMetaProps = TypographyProps &
  FileModel &
  FileListItemFileMetaOwnProps;

const UnifyFileListItemFileMetaRoot = styled(Typography, {
  name: 'UnifyFileListItem',
  slot: 'FileMeta',
  overridesResolver: (_, styles) => {
    return [
      styles.fileMeta,
      {
        [`&.${fileListItemClasses.fileMetaFileUploadStatus}`]:
          styles.fileMetaFileUploadStatus,
      },
      {
        [`&.${fileListItemClasses.fileMetaFileSize}`]: styles.fileMetaFileSize,
      },
    ];
  },
})<TypographyProps & { ownerState: { numericMeta: boolean } }>(
  ({ ownerState }) => {
    const { numericMeta } = ownerState;
    return {
      color: getToken(`Core/Text/text-secondary`),
      flexShrink: 0, // Don't allow the space between a file size and its unit to cause the text to break onto two lines
      ...(numericMeta && { fontVariantNumeric: 'tabular-nums' }),
    };
  }
);

export function FileListItemFileMeta(
  props: FileListItemFileMetaProps
): React.ReactElement | null {
  const {
    className: classNameProp,
    file,
    uploadPercentComplete = defaultFileUploadPercentComplete,
    showFileSize,
    ...rest
  } = props;
  const isUploading = fileIsUploading(uploadPercentComplete);
  const indeterminateUpload = fileUploadIsIndeterminate(uploadPercentComplete);

  let children: React.ReactNode;
  if (!isUploading) {
    if (!showFileSize) return null;
    children = formatFileSize(file.size);
  } else {
    // TODO: Internationalize the word "Uploading"
    children = indeterminateUpload ? 'Uploading' : `${uploadPercentComplete}%`;
  }

  const className = clsx(
    classNameProp,
    fileListItemClasses.fileMeta,
    isUploading && fileListItemClasses.fileMetaFileUploadStatus,
    !isUploading && showFileSize && fileListItemClasses.fileMetaFileSize
  );

  return (
    <UnifyFileListItemFileMetaRoot
      {...rest}
      className={className}
      variant="subtitle2"
      component="span"
      ownerState={{ numericMeta: !indeterminateUpload }}
    >
      {children}
    </UnifyFileListItemFileMetaRoot>
  );
}

export default FileListItemFileMeta;
